import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = window.__WR_API_BASE__ ? `${window.__WR_API_BASE__}/api` : '/api';
const AUTO_SAVE_TIMEOUT = 3 * 60 * 1000; // 3 minutes

// Module-scoped guard to detect React StrictMode dev double-mount and
// any in-tab remount (e.g., gate→main app transition). One JS context per
// browser tab, so this resets only on a brand-new tab open / hard reload.
// Combined with sessionStorage below to distinguish "new tab" vs "refresh".
let RESTORE_DIALOG_DECIDED = false;

/**
 * useAutosaveSession — Handles all autosave, session restore, team sharing, and guest session logic.
 * Extracted from App.js to reduce monolith.
 */
const useAutosaveSession = ({
  notes, result, isGenerating, effectiveRole, guestId,
  imagingOrders, specialties, patientAge, patientGender, patientStatus,
  chiefComplaint, manualIcd10, charLimit, letterType, letterContext,
  soldierRank, soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone,
  mtfName, customMtf, providerSignatureBlock, encounterType, visitType,
  includeROS, includePE, generateClinicalNote, generateAdminLetter, generateLimitations,
  occupation, jobDuties, selectedTemplate, patientLabel,
  user, loadHistory, trackFeature, restoreFormData,
  currentDraftId, setCurrentDraftId, autoSavedDraft, setAutoSavedDraft,
}) => {
  const [autoShareTeamId, setAutoShareTeamId] = useState(() => localStorage.getItem('writeright_auto_share_team') || null);
  const [autoShareDrafts, setAutoShareDrafts] = useState(() => localStorage.getItem('writeright_auto_share_drafts') === 'true');
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [pendingRestoreData, setPendingRestoreData] = useState(null);
  // Perf fix (Mar 3, 2026): lastActivityTime was a useState updated on
  // every keydown, mousedown, touchstart at the window level. Because App.js
  // consumes the useAutosaveSession return, every keystroke re-rendered the
  // ENTIRE app tree (including the 2000-line OutputDocumentsPanel), causing
  // the "typing lag" the provider reported that made the contentEditable
  // note feel read-only. Ref keeps the value read-write without triggering
  // a re-render. The 30s inactivity interval below reads the ref directly.
  const lastActivityTimeRef = useRef(Date.now());

  // Feb 28, 2026 — list of ALL teams the user belongs to. Used to broadcast
  // auto-stream to every assigned team when `user.default_auto_stream === true`
  // (per user request: "All teams I'm assigned to"). Fetched once on login.
  const [userTeams, setUserTeams] = useState([]);

  // Feb 28, 2026 — Re-sync autoShareDrafts from the profile-synced event so
  // that when /api/auth/me returns with default_auto_stream=true on a fresh
  // device, the team-share React state updates immediately (previously it
  // was stuck at false because useState only runs once at mount). Also re-
  // reads localStorage on any cross-tab storage event.
  useEffect(() => {
    const sync = () => {
      try {
        const v = localStorage.getItem('writeright_auto_share_drafts') === 'true';
        setAutoShareDrafts(v);
        const tid = localStorage.getItem('writeright_auto_share_team');
        if (tid) setAutoShareTeamId(tid);
      } catch { /* ignore */ }
    };
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:profile-synced', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:profile-synced', sync);
    };
  }, []);

  // Feb 28, 2026 — also react directly to the user prop. The profile-synced
  // event covers most cases, but useProfile may run its effect before this
  // hook's listeners attach. This belt-and-suspenders sync makes it robust.
  useEffect(() => {
    if (user?.default_auto_stream === true) {
      setAutoShareDrafts(true);
    }
  }, [user?.default_auto_stream]);

  // Feb 28, 2026 — fetch the user's assigned teams once after login so the
  // global auto-stream toggle can broadcast to ALL of them.
  useEffect(() => {
    if (!user) { setUserTeams([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/teams/list`, { withCredentials: true });
        if (!cancelled && Array.isArray(res.data?.teams)) {
          setUserTeams(res.data.teams.map(t => ({ id: t.id, name: t.name })));
        }
      } catch (e) { /* not a team member or auth issue — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Feb 28, 2026 — when the global "Auto-Stream" toggle is ON, the user
  // expects every assigned team to receive notes automatically. This is the
  // single source of truth (per user direction: "All teams I'm assigned to").
  // Treat `user.default_auto_stream === true` as "broadcast to all teams".
  const globalAutoStreamOn = user?.default_auto_stream === true;

  // Autosave data builder
  const buildAutosaveData = useCallback(() => ({
    notes, imagingOrders, specialties, patientAge, patientGender, patientStatus,
    chiefComplaint, manualIcd10, charLimit, letterType, letterContext, soldierRank,
    soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone, mtfName,
    customMtf, providerSignatureBlock, encounterType, visitType, includeROS, includePE,
    generateClinicalNote, generateAdminLetter, generateLimitations, occupation, jobDuties, result,
    savedAt: new Date().toISOString()
  }), [notes, imagingOrders, specialties, patientAge, patientGender, patientStatus,
      chiefComplaint, manualIcd10, charLimit, letterType, letterContext, soldierRank,
      soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone, mtfName,
      customMtf, providerSignatureBlock, encounterType, visitType, includeROS, includePE,
      generateClinicalNote, generateAdminLetter, generateLimitations, occupation, jobDuties, result]);

  // Debounced save on changes (5s)
  useEffect(() => {
    if (!notes || notes.trim().length < 10) return;
    const t = setTimeout(() => { localStorage.setItem('writeright_autosave', JSON.stringify(buildAutosaveData())); setLastAutoSave(new Date()); }, 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildAutosaveData]);

  // Interval save (10s)
  useEffect(() => {
    const i = setInterval(() => {
      if (notes && notes.trim().length >= 10) { localStorage.setItem('writeright_autosave', JSON.stringify(buildAutosaveData())); setLastAutoSave(new Date()); }
    }, 10000);
    return () => clearInterval(i);
  }, [notes, buildAutosaveData]);

  // Warn before unload
  useEffect(() => {
    const handler = (e) => {
      if ((notes && notes.trim().length >= 10) || result) {
        localStorage.setItem('writeright_autosave', JSON.stringify(buildAutosaveData()));
        e.preventDefault(); e.returnValue = ''; return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [notes, result, buildAutosaveData]);

  // Team sharing toggles
  const toggleAutoShareTeam = useCallback((teamId) => {
    trackFeature('auto_share_notes');
    if (autoShareTeamId === teamId) { setAutoShareTeamId(null); localStorage.removeItem('writeright_auto_share_team'); toast.info('Auto-share disabled'); }
    else { setAutoShareTeamId(teamId); localStorage.setItem('writeright_auto_share_team', teamId); toast.success('Auto-share enabled for this team'); }
  }, [autoShareTeamId, trackFeature]);

  const toggleAutoShareDrafts = useCallback(() => {
    trackFeature('draft_streaming');
    const next = !autoShareDrafts;
    setAutoShareDrafts(next); localStorage.setItem('writeright_auto_share_drafts', String(next));
    toast.info(next ? 'Draft streaming to team enabled' : 'Draft streaming disabled');
  }, [autoShareDrafts, trackFeature]);

  const quickShareToTeam = useCallback(async (noteId) => {
    // Feb 28, 2026 — if global auto-stream is ON, broadcast to ALL teams.
    if (globalAutoStreamOn && userTeams.length > 0) {
      try {
        await Promise.all(userTeams.map(t =>
          axios.post(`${API}/teams/${t.id}/share-note`, { note_id: noteId }, { withCredentials: true })
            .catch(e => console.error('Share fail', t.id, e))
        ));
        toast.success(`Shared to ${userTeams.length} team${userTeams.length === 1 ? '' : 's'}`);
        return;
      } catch (e) { toast.error('Some shares failed'); return; }
    }
    const teamId = autoShareTeamId || localStorage.getItem('writeright_active_team');
    if (!teamId) { toast.error('No active team — open Team Board first'); return; }
    try { await axios.post(`${API}/teams/${teamId}/share-note`, { note_id: noteId }, { withCredentials: true }); toast.success('Shared to team'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Share failed'); }
  }, [autoShareTeamId, globalAutoStreamOn, userTeams]);

  const shareDraftToTeam = useCallback(async (draftData) => {
    // Feb 28, 2026 — if global auto-stream is ON, broadcast draft to ALL teams.
    if (globalAutoStreamOn && userTeams.length > 0) {
      await Promise.all(userTeams.map(t =>
        axios.post(`${API}/teams/${t.id}/share-draft`, draftData, { withCredentials: true })
          .catch(e => console.error('Draft share fail', t.id, e))
      ));
      return;
    }
    if (!autoShareTeamId || !autoShareDrafts) return;
    try { await axios.post(`${API}/teams/${autoShareTeamId}/share-draft`, draftData, { withCredentials: true }); }
    catch (err) { console.error('Draft share failed:', err); }
  }, [autoShareTeamId, autoShareDrafts, globalAutoStreamOn, userTeams]);

  // Activity tracking for inactivity auto-save
  useEffect(() => {
    // Mar 3, 2026 — writes to a ref, not state, so keystrokes don't trigger
    // React re-renders. setAutoSavedDraft(false) is React-bail-outed when
    // the flag is already false, so it's safe to call unconditionally.
    const h = () => { lastActivityTimeRef.current = Date.now(); setAutoSavedDraft(false); };
    window.addEventListener('keydown', h); window.addEventListener('mousedown', h); window.addEventListener('touchstart', h);
    return () => { window.removeEventListener('keydown', h); window.removeEventListener('mousedown', h); window.removeEventListener('touchstart', h); };
  }, [setAutoSavedDraft]);

  // Auto-save draft on 3min inactivity
  useEffect(() => {
    if (effectiveRole !== 'provider' || !notes || notes.trim().length < 50 || result || autoSavedDraft || isGenerating) return;
    const check = setInterval(() => {
      if (Date.now() - lastActivityTimeRef.current >= AUTO_SAVE_TIMEOUT && !autoSavedDraft && !isGenerating && !result) {
        setAutoSavedDraft(true);
        (async () => {
          try {
            const ep = currentDraftId ? `${API}/history/update-draft` : `${API}/history/save-draft`;
            const res = await axios.post(ep, {
              notes, patient_age: patientAge ? parseInt(patientAge) : null, patient_gender: patientGender || null,
              patient_status: patientStatus || null, chief_complaint: chiefComplaint || null,
              imaging_orders: imagingOrders.filter(i => i.type?.trim()) || null,
              specialties: specialties.filter(s => s?.trim()) || null,
              icd10_codes: manualIcd10 || null, guest_id: guestId,
              ...(currentDraftId && { draft_id: currentDraftId })
            }, { withCredentials: true });
            if (res.data.success) {
              if (res.data.draft_id && !currentDraftId) setCurrentDraftId(res.data.draft_id);
              toast.success(currentDraftId ? 'Draft updated!' : 'Draft auto-saved!', { duration: 3000 });
              loadHistory();
              // Feb 28, 2026 — broadcast to ALL teams when global auto-stream
              // is ON (per user direction), otherwise fall back to per-team
              // selection. Both paths handled inside shareDraftToTeam.
              if ((globalAutoStreamOn && userTeams.length > 0) || (autoShareDrafts && autoShareTeamId)) {
                shareDraftToTeam({ draft_id: res.data.draft_id || currentDraftId, notes, patient_label: patientLabel, patient_age: patientAge, patient_gender: patientGender, chief_complaint: chiefComplaint });
              }
            }
          } catch (e) { console.error('Auto-save failed:', e); }
        })();
      }
    }, 30000);
    return () => clearInterval(check);
  }, [notes, result, autoSavedDraft, isGenerating, effectiveRole,
      specialties, imagingOrders, patientAge, patientGender, patientStatus, chiefComplaint,
      manualIcd10, guestId, currentDraftId, loadHistory, autoShareDrafts, autoShareTeamId,
      shareDraftToTeam, patientLabel, globalAutoStreamOn, userTeams, setAutoSavedDraft]);

  // Restore dialog on mount
  // SAFETY FIX (May 5-6, 2026): Never auto-prompt restore on a brand-new
  // browser tab. Two-layer guard:
  //   1. Module-scoped `RESTORE_DIALOG_DECIDED` — survives React StrictMode
  //      double-mount and any in-tab remount (e.g., gate→main-app transition)
  //      within a single JS context. Resets only on hard reload / new tab.
  //   2. sessionStorage `writeright_tab_initialized` — survives page reload
  //      (so F5 still gets the restore dialog) but NOT new-tab open. This
  //      lets us tell "same tab refresh" apart from "new tab open".
  // On a fresh tab, both are unset → we suppress the dialog AND proactively
  // clear `writeright_autosave` for THIS tab so any subsequent in-tab logic
  // can't re-pull the prior patient's demographics. The original tab's
  // localStorage will be re-written by its own ongoing autosave loop.
  useEffect(() => {
    if (RESTORE_DIALOG_DECIDED) return;
    RESTORE_DIALOG_DECIDED = true;
    const TAB_FLAG = 'writeright_tab_initialized';
    const sessionInitialized = sessionStorage.getItem(TAB_FLAG);
    sessionStorage.setItem(TAB_FLAG, '1');
    if (!sessionInitialized) {
      // BRAND-NEW TAB — never auto-prompt restore. Do NOT touch localStorage
      // (the original tab may still own that autosave). Patient demographic
      // setters were never called → fields remain blank as intended.
      return;
    }
    // Same-tab reload (F5) — show restore dialog as before.
    const saved = localStorage.getItem('writeright_autosave');
    if (saved) {
      try { const d = JSON.parse(saved); if (d.notes?.trim().length >= 10) { setPendingRestoreData(d); setShowRestoreDialog(true); } else localStorage.removeItem('writeright_autosave'); }
      catch (e) { localStorage.removeItem('writeright_autosave'); }
    }
  }, []);

  const handleRestoreChoice = useCallback((choice) => {
    if (choice === 'restore' && pendingRestoreData) { restoreFormData(pendingRestoreData); toast.success('Session restored!', { duration: 2000 }); }
    else if (choice === 'done') { localStorage.removeItem('writeright_autosave'); toast.success('Cleared for new patient', { duration: 2000 }); }
    else if (choice === 'history') { localStorage.removeItem('writeright_autosave'); toast.success('Opening your history...', { duration: 2000 }); }
    else if (choice === 'discard') { localStorage.removeItem('writeright_autosave'); toast.info('Saved data discarded', { duration: 1500 }); }
    setShowRestoreDialog(false); setPendingRestoreData(null);
  }, [pendingRestoreData, restoreFormData]);

  const saveGuestSession = useCallback(() => {
    localStorage.setItem('writeright_guest_session', JSON.stringify(buildAutosaveData()));
  }, [buildAutosaveData]);

  const restoreGuestSession = useCallback(() => {
    const s = localStorage.getItem('writeright_guest_session');
    if (s) { try { restoreFormData(JSON.parse(s)); localStorage.removeItem('writeright_guest_session'); toast.success('Your session has been restored!'); } catch (e) {} }
  }, [restoreFormData]);

  // Restore after OAuth
  useEffect(() => {
    const shouldRestore = localStorage.getItem('writeright_pending_restore');
    if (shouldRestore && user) {
      localStorage.removeItem('writeright_pending_restore');
      const gs = localStorage.getItem('writeright_guest_session');
      if (gs) restoreGuestSession();
      else { const a = localStorage.getItem('writeright_autosave'); if (a) { try { const d = JSON.parse(a); if (d.notes?.trim().length > 10) { restoreFormData(d); toast.success('Your work has been restored from autosave!'); } } catch (e) {} } }
    }
  }, [user, restoreGuestSession, restoreFormData]);

  return {
    autoShareTeamId, autoShareDrafts,
    lastAutoSave,
    showRestoreDialog, pendingRestoreData,
    handleRestoreChoice,
    toggleAutoShareTeam, toggleAutoShareDrafts,
    quickShareToTeam, shareDraftToTeam,
    saveGuestSession, restoreGuestSession,
    buildAutosaveData,
    // Feb 28, 2026 — exposed for TeamBoardSlidePanel pills so they can show
    // "ON" for every team when global auto-stream is enabled in Profile.
    globalAutoStreamOn, userTeams,
  };
};

export default useAutosaveSession;
